import { COMMERCIAL_CALIBRATORS, hasMinRole, type Role } from "@/lib/roles";
import type { ProjectArea } from "@/lib/projectSource";

/** Uma tela do catálogo e quem a alcança. */
type ProjectRouteEntry = {
  slug: string;
  minRole: Role;
  /**
   * Lista fechada de papéis aceitos, além do mínimo, para a regra que a escada
   * não expressa. Espelha o `only` do `require_project_access` no backend.
   */
  only?: readonly Role[];
  /** `false`: a aba não serve de tela inicial, e o `projectHomePath` a pula. */
  landing?: boolean;
};

/**
 * As sub-telas de um projeto que a sidebar mostra, e o papel mínimo de cada uma.
 *
 * Um projeto não é uma tela só: abrir um deles abre oito (sete quando esta
 * lista nasceu; a Calibragem entrou depois, #877), e até aqui **todas
 * apareciam para todo mundo**. Um `user` — que no vocabulário do produto é
 * quem "lê o que lhe deram" — via Runs, Artifacts, Connections, Cost e Settings
 * do projeto inteiro. O Diagnóstico é a única delas que existe para ele.
 *
 * ⚠️ **Não é autorização.** Quem autoriza é o servidor, em toda requisição. Isto
 * decide o que a tela oferece, não o que o backend entrega — e as duas coisas não
 * coincidem: várias leituras dessas telas são `require_min_role(roles.USER)` no
 * servidor. Esconder a tela é decisão de produto ("essa pessoa não tem o que fazer
 * aqui"), não um cadeado.
 *
 * A **ordem importa duas vezes**: é a ordem da sidebar, e é a ordem em que
 * `projectHomePath` procura onde aterrissar. Mover o Diagnóstico para cima
 * mudaria a tela inicial de todo mundo.
 *
 * **Por que `cost` e `settings` param em `manager`:** o consultor trabalha dentro
 * do projeto — roda agente, mexe em conexão, lê artefato. Quanto o projeto custou
 * e quem tem acesso a ele são assunto de quem responde pelo projeto, não de quem
 * o executa. `settings` é também onde se atribui e revoga gente, e o backend já
 * exige `manager` para isso.
 */
export const PROJECT_TABS = [
  { slug: "", minRole: "consultant" },
  { slug: "runs", minRole: "consultant" },
  { slug: "artifacts", minRole: "consultant" },
  { slug: "connections", minRole: "consultant" },
  // A Calibragem vem imediatamente acima do Diagnóstico: a posição carrega o
  // sentido do fluxo, o que se calibra vem antes do que se apresenta. É
  // `consultant` porque quem lê o Diagnóstico não trabalha o número, então quem
  // é `user` continua aterrissando no Diagnóstico e nada muda para ele.
  { slug: "calibration", minRole: "consultant" },
  // EF-FUNC-012 v0.1 RF-01: logo abaixo da Calibragem, só para Vendas e
  // Administrador. O consultor e o manager estão acima do comercial na escada e
  // ficam de fora, por isso a lista. Não é tela inicial: o comercial continua
  // abrindo o projeto no Diagnóstico, que vem logo depois.
  {
    slug: "commercial-calibration",
    minRole: "sales",
    only: COMMERCIAL_CALIBRATORS,
    landing: false,
  },
  { slug: "diagnostic", minRole: "user" },
  { slug: "cost", minRole: "manager" },
  { slug: "settings", minRole: "manager" },
] as const satisfies readonly ProjectRouteEntry[];

/**
 * As telas de trabalho do projeto: os workbenches de agente e o Code Companion.
 *
 * Ficam **fora** de `PROJECT_TABS` porque não são abas — não aparecem na sidebar,
 * e só se chega nelas pela Overview (pelo DAG do fluxo, pela espinha da migração
 * ou pelo painel de orquestração). Mas rota é rota: sem guarda, uma URL colada
 * abria qualquer uma delas.
 *
 * ⚠️ **Isto fecha uma porta que o backend deixa aberta de propósito.** Executar
 * agente é `CONSULTANT` no servidor, mas *ler* o quadro do Code Companion é
 * `USER` — há comentário explícito em `code_companion_context.py` dizendo "dev vê
 * o board read-only". Um desenvolvedor externo com papel `user` perde o quadro
 * aqui. Foi decisão consciente: quem precisa do quadro deve ser `consultant`. Se
 * o papel `user` voltar a ser o do desenvolvedor externo, o Code Companion é a
 * primeira linha a rever.
 */
const CONFIG_SERVER_WORKBENCH = { slug: "agents/configuration-server", minRole: "consultant" } as const;

export const PROJECT_WORKBENCHES = [
  { slug: "agents/architect", minRole: "consultant" },
  CONFIG_SERVER_WORKBENCH,
  { slug: "agents/functional", minRole: "consultant" },
  { slug: "agents/code-companion", minRole: "consultant" },
] as const satisfies readonly ProjectRouteEntry[];

/** As etapas reutilizam o papel mínimo da central no catálogo de alcance. */
export const PROJECT_STAGE_ROUTES = [
  { slug: "agents/configuration-server/management", minRole: CONFIG_SERVER_WORKBENCH.minRole },
  { slug: "agents/configuration-server/extraction", minRole: CONFIG_SERVER_WORKBENCH.minRole },
  { slug: "agents/configuration-server/execution", minRole: CONFIG_SERVER_WORKBENCH.minRole },
] as const satisfies readonly ProjectRouteEntry[];

/**
 * As telas que moram dentro de uma aba sem serem aba. A montagem do PDF do cliente
 * (#1772) fica no Diagnóstico, mas gera o PDF, e a rota do servidor exige do
 * comercial para cima; o mínimo aqui é o mesmo, para quem não gera nem abrir.
 */
export const PROJECT_SUBPAGES = [
  { slug: "diagnostic/pdf", minRole: "sales" },
] as const satisfies readonly ProjectRouteEntry[];

/** Toda rota debaixo de `/projects/:projectId`. Nenhuma fica de fora do guarda. */
export const PROJECT_ROUTES = [
  ...PROJECT_TABS,
  ...PROJECT_WORKBENCHES,
  ...PROJECT_STAGE_ROUTES,
  ...PROJECT_SUBPAGES,
];

/** `""` é a Overview — a rota `/projects/:projectId` sem sufixo nenhum. */
export type ProjectTabSlug = (typeof PROJECT_TABS)[number]["slug"];
export type ProjectWorkbenchSlug = (typeof PROJECT_WORKBENCHES)[number]["slug"];
export type ProjectStageSlug = (typeof PROJECT_STAGE_ROUTES)[number]["slug"];
export type ProjectSubpageSlug = (typeof PROJECT_SUBPAGES)[number]["slug"];
export type ProjectRouteSlug =
  | ProjectTabSlug
  | ProjectWorkbenchSlug
  | ProjectStageSlug
  | ProjectSubpageSlug;

/**
 * A área de cada workbench que só existe para origem SAP (#1784). Projeto de outra origem não
 * tem o card no pipeline, e a URL colada volta para a visão geral do projeto.
 */
export const PROJECT_ROUTE_AREA: Partial<Record<ProjectRouteSlug, ProjectArea>> = {
  "agents/architect": "architect",
  "agents/code-companion": "code_companion",
  "agents/configuration-server": "configuration_server",
  "agents/configuration-server/management": "configuration_server",
  "agents/configuration-server/extraction": "configuration_server",
  "agents/configuration-server/execution": "configuration_server",
};

/** A URL da tela. Existe para que rota, link da sidebar e redirect não divirjam. */
export function projectRoutePath(projectId: string, slug: ProjectRouteSlug): string {
  return slug === "" ? `/projects/${projectId}` : `/projects/${projectId}/${slug}`;
}

/** Fail-closed: slug fora do catálogo não é tela, e papel fora do vocabulário vale 0. */
export function canReachProjectRoute(
  role: string | null | undefined,
  slug: ProjectRouteSlug,
): boolean {
  const route: ProjectRouteEntry | undefined = PROJECT_ROUTES.find((r) => r.slug === slug);
  if (route == null || !hasMinRole(role, route.minRole)) return false;
  return route.only == null || (route.only as readonly string[]).includes(role ?? "");
}

/**
 * Onde `role` cai ao abrir um projeto: a **primeira aba** do catálogo que alcança.
 *
 * Só abas — ninguém aterrissa num workbench. Derivar da mesma lista em vez de
 * escrever `role === "user" ? diagnostic : overview` fecha um laço de redirect por
 * construção: a aba devolvida é, por definição, uma que passa no
 * `canReachProjectRoute`, então ela nunca redireciona de volta.
 *
 * Sem nenhuma aba alcançável (papel corrompido, que vale 0 e não satisfaz nem
 * `user`) a resposta é a lista de projetos: uma volta, não um ciclo.
 */
export function projectHomePath(role: string | null | undefined, projectId: string): string {
  const home = PROJECT_TABS.find(
    (tab: ProjectRouteEntry) => tab.landing !== false && canReachProjectRoute(role, tab.slug as ProjectRouteSlug),
  );
  return home ? projectRoutePath(projectId, home.slug) : "/projects";
}
